import type { PuzzleModel } from '@arrow/game-engine';
import type { Clue } from '@arrow/puzzle-schema';
import { type CSSProperties, memo, useMemo } from 'react';

import { clueSlot } from '../game/view.ts';

/**
 * The bare board: the puzzle's geometry and arrows with every letter and clue lifted off,
 * so it can sit on the result poster (and in a screenshot) without spoiling anything.
 * The best Flow chain is drawn over it in cobalt: a route through each answer's midpoint
 * for a short chain, the answers themselves lit as bars for a long one, and a lighter
 * "all lit" treatment when the chain covers (nearly) the whole board (`routeStyle`).
 *
 * It renders NO letters and NO clue text: only cell kinds, clue directions and the chain.
 * One cell is `U` units; the SVG scales to whatever box CSS gives it (`.bmap`). The route
 * is a drawing over the map, so on a big board it is thickened by `mapScale` to keep the
 * weight it has on an 8×8 (its cells are smaller on the same poster).
 */
const U = 100;
const f = (n: number) => Math.round(n * 10) / 10;

interface BoardMapProps {
	model: PuzzleModel;
	/** Answer ids of the chain to draw, in solve order. Empty or one answer: no route. */
	chain?: readonly string[];
	/** Answer ids the player revealed: their cells are tinted in the reveal red, under the rules. */
	revealed?: readonly string[];
	/** Draw the route on (stroke draw, then the dots). False: drawn in its final state. */
	animate?: boolean;
	/** How to draw the chain: `auto` (the default) picks by length, see `routeStyle`. */
	routeAs?: 'auto' | RouteStyle;
	/** Accessible description; the map is decorative when omitted. */
	label?: string;
	className?: string;
	style?: CSSProperties;
}

export const BoardMap = memo(function BoardMap({ model, chain = [], revealed = [], animate = false, routeAs = 'auto', label, className, style }: BoardMapProps) {
	const W = model.width * U;
	const H = model.height * U;

	const blocks = useMemo(() => {
		const out: { x: number; y: number; kind: 'clue' | 'blank' }[] = [];
		model.kinds.forEach((kind, i) => {
			if (kind === 'letter') return;
			out.push({ x: (i % model.width) * U, y: Math.floor(i / model.width) * U, kind });
		});
		return out;
	}, [model]);

	const rules = useMemo(() => {
		let d = '';
		for (let c = 1; c < model.width; c++) d += `M${c * U} 0V${H}`;
		for (let r = 1; r < model.height; r++) d += `M0 ${r * U}H${W}`;
		return d;
	}, [model, W, H]);

	const clues = useMemo(() => Object.values(model.clues), [model]);
	const revealedCells = useMemo(() => {
		const out = new Set<number>();
		for (const id of revealed) for (const cell of model.answers[id]?.cells ?? []) out.add(cell);
		return [...out];
	}, [model, revealed]);
	const inChain = useMemo(() => new Set(chain), [chain]);
	const as = routeAs === 'auto' ? routeStyle(chain.length, model.answerOrder.length) : routeAs;
	const route = useMemo(() => (as === 'line' ? routePoints(model, chain) : []), [model, chain, as]);
	const bars = useMemo(() => (as === 'line' ? [] : answerBars(model, chain)), [model, chain, as]);
	const first = bars[0];
	const last = bars[bars.length - 1];

	const cls = ['bmap', animate ? 'draw' : '', className ?? ''].filter(Boolean).join(' ');
	const k = mapScale(model);
	const vars = { '--map-k': k, ...style } as CSSProperties;
	return (
		<svg
			className={cls}
			style={vars}
			viewBox={`0 0 ${W} ${H}`}
			role={label ? 'img' : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : true}
			data-testid="board-map"
		>
			<rect className="bmap-paper" x={0} y={0} width={W} height={H} />
			{blocks.map((b) => (
				<rect key={`${b.x}-${b.y}`} className={`bmap-${b.kind}`} x={b.x} y={b.y} width={U} height={U} />
			))}
			{revealedCells.map((i) => (
				<rect key={`r${i}`} className="bmap-revealed" x={(i % model.width) * U} y={Math.floor(i / model.width) * U} width={U} height={U} />
			))}
			<path className="bmap-rules" d={rules} />
			{blocks.map((b) =>
				b.kind === 'clue' ? <rect key={`e${b.x}-${b.y}`} className="bmap-edge" x={b.x + 5} y={b.y + 5} width={U - 10} height={U - 10} /> : null,
			)}
			<g className="bmap-arrows">
				{clues.map((clue) => (
					<MapArrow key={clue.id} model={model} clue={clue} best={inChain.has(clue.answerId)} />
				))}
			</g>
			<rect className="bmap-frame" x={0} y={0} width={W} height={H} />
			{as === 'bars' && bars.length > 1 && (
				<g className="bmap-route bmap-bars" data-chain={chain.join(' ')}>
					{bars.map(([x1, y1, x2, y2], i) => (
						<path key={i} className="bmap-bar" pathLength={1} d={`M${x1} ${y1}L${x2} ${y2}`} style={{ '--k': i } as CSSProperties} />
					))}
				</g>
			)}
			{as === 'whole' && bars.length > 1 && first && last && (
				<g className="bmap-route bmap-whole" data-chain={chain.join(' ')}>
					{/* The board itself is lit: a cobalt keyline just inside the ink frame. */}
					<rect className="bmap-lit" x={KEY_IN} y={KEY_IN} width={W - 2 * KEY_IN} height={H - 2 * KEY_IN} />
					{bars.map(([x1, y1, x2, y2], i) => (
						<path key={i} className="bmap-bar" pathLength={1} d={`M${x1} ${y1}L${x2} ${y2}`} style={{ '--k': i } as CSSProperties} />
					))}
					{/* Where the chain started and where it ended carry the weight the bars give up. */}
					<circle className="bmap-end start" cx={first[0]} cy={first[1]} r={f(U * 0.17 * k)} />
					<circle className="bmap-end" cx={last[2]} cy={last[3]} r={f(U * 0.17 * k)} style={{ '--k': bars.length } as CSSProperties} />
				</g>
			)}
			{route.length > 1 && (
				<g className="bmap-route" data-chain={chain.join(' ')}>
					<path className="bmap-line" pathLength={1} d={`M${route.map(([x, y]) => `${x} ${y}`).join('L')}`} />
					{route.map(([x, y], i) => (
						<circle key={i} className="bmap-dot" cx={x} cy={y} r={f(U * 0.11 * k)} style={{ '--k': i } as CSSProperties} />
					))}
				</g>
			)}
		</svg>
	);
});

export type RouteStyle = 'line' | 'bars' | 'whole';

/** The cobalt keyline's inset on a whole-board chain, in board units (a cell is 100). */
const KEY_IN = 9;

/** The board size the route's weights are set for: an 8×8. */
const BASE_SIDE = 8;

/**
 * How much heavier to draw the route on this board, so it keeps an 8×8's weight when the
 * board is bigger (a 13×13 on the same poster has cells ~0.6× the size). Never lighter.
 */
export function mapScale(model: Pick<PuzzleModel, 'width' | 'height'>): number {
	return Math.round(Math.max(1, Math.max(model.width, model.height) / BASE_SIDE) * 100) / 100;
}

/** From this many answers, a chain is drawn as answer bars rather than a route. */
export const LONG_CHAIN = 8;

/** A long chain covering at least this share of the board's answers is a whole-board chain. */
export const WHOLE_BOARD = 0.8;

/**
 * A short chain is a route through each answer's midpoint, in solve order: a few hops
 * read as a path. A long one would tangle into a web, so it is drawn as the answers
 * themselves, lit on the board: bars that lie on the grid's own rows and columns, so they
 * cross like the words do and can never tangle.
 *
 * A long chain that covers (nearly) every answer (`WHOLE_BOARD`) would paint the whole
 * board solid cobalt, so it is drawn lighter: thin, translucent bars, its start and end
 * marked, and a cobalt keyline round the board ("all lit"). A short board's whole chain is
 * still a few hops, so it stays a route.
 */
export function routeStyle(length: number, answers = 0): RouteStyle {
	if (length < LONG_CHAIN) return 'line';
	return answers > 0 && length / answers >= WHOLE_BOARD ? 'whole' : 'bars';
}

/** Each chain answer as a segment from its first cell's centre to its last's, in chain order. */
export function answerBars(model: PuzzleModel, chain: readonly string[]): [number, number, number, number][] {
	const out: [number, number, number, number][] = [];
	for (const id of chain) {
		const cells = model.answers[id]?.cells;
		if (!cells?.length) continue;
		const a = cells[0]!;
		const b = cells[cells.length - 1]!;
		const c = (i: number) => [(i % model.width) * U + U / 2, Math.floor(i / model.width) * U + U / 2] as const;
		const [x1, y1] = c(a);
		const [x2, y2] = c(b);
		out.push([x1, y1, x2, y2]);
	}
	return out;
}

/** Each answer's midpoint, in chain order (the mock's `drawChain`). */
export function routePoints(model: PuzzleModel, chain: readonly string[]): [number, number][] {
	const pts: [number, number][] = [];
	for (const id of chain) {
		const cells = model.answers[id]?.cells;
		if (!cells?.length) continue;
		const a = cells[0]!;
		const b = cells[cells.length - 1]!;
		const x = (((a % model.width) + (b % model.width)) / 2) * U + U / 2;
		const y = ((Math.floor(a / model.width) + Math.floor(b / model.width)) / 2) * U + U / 2;
		pts.push([f(x), f(y)]);
	}
	return pts;
}

/** A solid ink wedge growing out of the clue block, pointing along its answer. */
const WEDGE_L = 17;
const WEDGE_H = 14;

function wedge(x: number, y: number, dir: 'right' | 'down', className: string) {
	const d =
		dir === 'right'
			? `M${f(x)} ${f(y - WEDGE_H)}L${f(x + WEDGE_L)} ${f(y)}L${f(x)} ${f(y + WEDGE_H)}Z`
			: `M${f(x - WEDGE_H)} ${f(y)}L${f(x)} ${f(y + WEDGE_L)}L${f(x + WEDGE_H)} ${f(y)}Z`;
	return <path className={className} d={d} />;
}

function MapArrow({ model, clue, best }: { model: PuzzleModel; clue: Clue; best: boolean }) {
	const { slot, count } = clueSlot(model, clue);
	const X = clue.cell.col * U;
	const Y = clue.cell.row * U;
	const cls = best ? 'bmap-arw best' : 'bmap-arw';
	const half = count === 1 ? U / 2 : slot === 0 ? U * 0.25 : U * 0.75;
	switch (clue.direction) {
		case 'right':
			return wedge(X + U, Y + half, 'right', cls);
		case 'down':
			return wedge(X + U / 2, Y + U, 'down', cls);
		case 'down-right': {
			// Into the cell below, then across: a short bent stem on that cell's top-left.
			const x0 = X + 16;
			const y1 = Y + U + 22;
			return (
				<g className={cls}>
					<path className="bmap-stem" d={`M${x0} ${Y + U}V${y1}H${x0 + 8}`} />
					{wedge(x0 + 8, y1, 'right', '')}
				</g>
			);
		}
		case 'right-down': {
			const y0 = Y + (count === 1 || slot === 0 ? 16 : U / 2 + 16);
			const x1 = X + U + 22;
			return (
				<g className={cls}>
					<path className="bmap-stem" d={`M${X + U} ${y0}H${x1}V${y0 + 8}`} />
					{wedge(x1, y0 + 8, 'down', '')}
				</g>
			);
		}
	}
}
